import { config } from "../config";
import { ErrorCode } from "./errorCodes";

type LogLevel = "error" | "warn";
type LogType = "api" | "wallet" | "ui" | "redux" | "walletconnect" | "unknown";

interface LogEntry {
  level: LogLevel;
  type: LogType;
  code: ErrorCode;
  message: string;
  error?: string;
  stack?: string;
  walletAddress?: string;
  page: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

const FLUSH_URL = `${config.proxyUrl}/logs/log-dapp-errors`;
const MAX_QUEUE = 100;
const MAX_RETRIES = 3;
const BASE_RETRY_MS = 1000;

class Logger {
  private queue: LogEntry[] = [];
  private walletAddress: string | undefined;
  private flushing = false;

  setWalletAddress(addr: string | undefined) {
    this.walletAddress = addr;
  }

  error(
    type: LogType,
    code: ErrorCode,
    message: string,
    err?: unknown,
    context?: Record<string, unknown>
  ) {
    this.log("error", type, code, message, err, context);
  }

  warn(type: LogType, code: ErrorCode, message: string, context?: Record<string, unknown>) {
    this.log("warn", type, code, message, undefined, context);
  }

  private log(
    level: LogLevel,
    type: LogType,
    code: ErrorCode,
    message: string,
    err?: unknown,
    context?: Record<string, unknown>
  ) {
    const entry: LogEntry = {
      level,
      type,
      code,
      message,
      walletAddress: this.walletAddress,
      page: typeof window !== "undefined" ? window.location.pathname : "",
      timestamp: new Date().toISOString(),
      ...(context && { context }),
    };

    if (err instanceof Error) {
      entry.error = err.message;
      if (import.meta.env.PROD && err.stack) {
        entry.stack = err.stack;
      }
    } else if (err !== undefined) {
      entry.error = String(err);
    }

    if (import.meta.env.DEV) {
      const method = level === "error" ? console.error : console.warn;
      method(`[${type.toUpperCase()}] ${code}`, message, err ?? "", context ?? "");
      return;
    }

    if (this.queue.length < MAX_QUEUE) {
      this.queue.push(entry);
    }
    this.scheduleFlush();
  }

  private scheduleFlush() {
    if (this.flushing) return;
    // Microtask to batch multiple synchronous log calls into one flush
    Promise.resolve().then(() => this.flush());
  }

  async flush(retryCount = 0): Promise<void> {
    if (this.queue.length === 0) return;
    this.flushing = true;

    const batch = this.queue.splice(0, 50);

    try {
      const res = await fetch(FLUSH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      if (this.queue.length > 0) {
        this.flushing = false;
        this.scheduleFlush();
      } else {
        this.flushing = false;
      }
    } catch (e) {
      console.error("[logger] flush failed:", e);
      if (retryCount < MAX_RETRIES) {
        const delay = BASE_RETRY_MS * Math.pow(2, retryCount);
        setTimeout(() => this.flush(retryCount + 1), delay);
      } else {
        this.queue.unshift(...batch);
        this.flushing = false;
      }
    }
  }

  flushSync() {
    if (this.queue.length === 0) {
      console.log("[logger] flushSync: queue empty");
      return;
    }
    const batch = this.queue.splice(0, 50);
    const ok = navigator.sendBeacon(
      FLUSH_URL,
      new Blob([JSON.stringify(batch)], { type: "application/json" })
    );
    console.log("[logger] sendBeacon result:", ok, "url:", FLUSH_URL);
  }
}

export const logger = new Logger();

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => logger.flushSync());
}
